// What a loop depends on: shared vocabulary for the scan contract, the worker, and the client.
// Secrets are recorded by name only; a value never travels through LoopGroup.

export const DEPENDENCY_KINDS = ["mcp_server", "plugin", "skill", "api", "secret", "file", "repository", "model", "other"] as const;
export type DependencyKind = (typeof DEPENDENCY_KINDS)[number];

export const DEPENDENCY_CONFIDENCES = ["confirmed", "inferred", "declared"] as const;
export type DependencyConfidence = (typeof DEPENDENCY_CONFIDENCES)[number];

export const DEPENDENCY_SOURCES = ["scan", "definition", "user", "agent"] as const;
export type DependencySource = (typeof DEPENDENCY_SOURCES)[number];

/** A dependency as reported by an agent or declared by a person. */
export interface DependencyInput {
  kind: DependencyKind | string;
  name: string;
  ref?: string | null;
  required?: boolean;
  confidence?: DependencyConfidence | string;
  note?: string | null;
}

/** A dependency as the API presents it. `providers` lists the agent environments that observed or confirmed it. */
export interface LoopDependency {
  id: string;
  loopId: string;
  kind: DependencyKind;
  name: string;
  ref: string | null;
  required: boolean;
  confidence: DependencyConfidence;
  source: DependencySource;
  provider: string | null;
  definitionId: string | null;
  note: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

/** Dependencies grouped by identity (kind + name) with every environment that reported them. */
export interface DependencyGroup {
  kind: DependencyKind;
  name: string;
  ref: string | null;
  required: boolean;
  confidence: DependencyConfidence;
  providers: string[];
  sources: DependencySource[];
  entries: LoopDependency[];
}

export const dependencyKindLabels: Record<DependencyKind, string> = {
  mcp_server: "MCP server",
  plugin: "Plugin",
  skill: "Skill",
  api: "API or service",
  secret: "Secret",
  file: "File",
  repository: "Repository",
  model: "Model",
  other: "Other",
};

/** The five families the UI groups by: tools, services, secrets, code and data, models. */
export type DependencyFamily = "tools" | "services" | "secrets" | "code" | "models" | "other";
export const dependencyFamilyLabels: Record<DependencyFamily, string> = { tools: "Tools", services: "Services", secrets: "Secrets", code: "Code and data", models: "Models", other: "Other" };

export function dependencyFamily(kind: DependencyKind): DependencyFamily {
  if (kind === "mcp_server" || kind === "plugin" || kind === "skill") return "tools";
  if (kind === "api") return "services";
  if (kind === "secret") return "secrets";
  if (kind === "file" || kind === "repository") return "code";
  if (kind === "model") return "models";
  return "other";
}

const KIND_ALIASES: Record<string, DependencyKind> = {
  mcp: "mcp_server",
  "mcp-server": "mcp_server",
  mcpserver: "mcp_server",
  server: "mcp_server",
  tool: "plugin",
  extension: "plugin",
  integration: "api",
  service: "api",
  webhook: "api",
  http: "api",
  credential: "secret",
  token: "secret",
  key: "secret",
  env: "secret",
  path: "file",
  document: "file",
  repo: "repository",
  git: "repository",
  llm: "model",
};

const SECRET_VALUE = /\b(sk|pk|lg|ghp|xox[abp]|AKIA)[A-Za-z0-9_-]{8,}\b|[A-Za-z0-9+/=]{40,}/;

export function normalizeDependencyKind(value: string | null | undefined): DependencyKind {
  const key = (value ?? "").trim().toLowerCase().replace(/\s+/g, "_");
  if ((DEPENDENCY_KINDS as readonly string[]).includes(key)) return key as DependencyKind;
  return KIND_ALIASES[key] ?? KIND_ALIASES[key.replace(/_/g, "-")] ?? "other";
}

export function normalizeDependencyConfidence(value: string | null | undefined, fallback: DependencyConfidence): DependencyConfidence {
  const key = (value ?? "").trim().toLowerCase();
  return (DEPENDENCY_CONFIDENCES as readonly string[]).includes(key) ? (key as DependencyConfidence) : fallback;
}

export type NormalizedDependency = { kind: DependencyKind; name: string; ref: string | null; required: boolean; confidence: DependencyConfidence; note: string | null };

/**
 * Cleans one reported dependency. Returns null when the name is empty or when the name or ref looks like
 * a secret value rather than a secret's name, so a careless agent cannot leak a token through this path.
 */
export function normalizeDependency(input: DependencyInput, fallbackConfidence: DependencyConfidence = "declared"): NormalizedDependency | null {
  const name = (input.name ?? "").trim().replace(/\s+/g, " ").slice(0, 120);
  if (!name) return null;
  const ref = (input.ref ?? "").trim().slice(0, 300) || null;
  if (SECRET_VALUE.test(name) || (ref && SECRET_VALUE.test(ref))) return null;
  const note = (input.note ?? "").trim().slice(0, 300) || null;
  return {
    kind: normalizeDependencyKind(input.kind),
    name,
    ref,
    required: input.required !== false,
    confidence: normalizeDependencyConfidence(input.confidence, fallbackConfidence),
    note,
  };
}

/** Identity of a dependency across environments and sources. */
export function dependencyKey(dependency: { kind: string; name: string }): string {
  return `${dependency.kind}:${dependency.name.trim().toLowerCase()}`;
}

/** Groups entries by identity; required wins over optional, confirmed over inferred over declared, and the first non-empty ref is kept. */
export function groupDependencies(entries: LoopDependency[]): DependencyGroup[] {
  const rank: Record<DependencyConfidence, number> = { confirmed: 3, inferred: 2, declared: 1 };
  const groups = new Map<string, DependencyGroup>();
  for (const entry of entries) {
    const key = dependencyKey(entry);
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { kind: entry.kind, name: entry.name, ref: entry.ref, required: entry.required, confidence: entry.confidence, providers: entry.provider ? [entry.provider] : [], sources: [entry.source], entries: [entry] });
      continue;
    }
    existing.entries.push(entry);
    existing.required = existing.required || entry.required;
    if (rank[entry.confidence] > rank[existing.confidence]) existing.confidence = entry.confidence;
    if (!existing.ref && entry.ref) existing.ref = entry.ref;
    if (entry.provider && !existing.providers.includes(entry.provider)) existing.providers.push(entry.provider);
    if (!existing.sources.includes(entry.source)) existing.sources.push(entry.source);
  }
  const order = ["tools", "services", "secrets", "code", "models", "other"];
  return [...groups.values()].sort((a, b) => {
    const family = order.indexOf(dependencyFamily(a.kind)) - order.indexOf(dependencyFamily(b.kind));
    if (family !== 0) return family;
    if (a.required !== b.required) return a.required ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

/** For a move: which dependencies the target environment has not observed or confirmed yet. */
export function unconfirmedFor(groups: DependencyGroup[], provider: string): DependencyGroup[] {
  return groups.filter((group) => !group.providers.includes(provider));
}
